import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ButtonGroup, Modal, Button } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import DatePickerRanger from "jsx/components/CitoPlus/components/DatePickerRanger";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import Tables from "jsx/components/CitoPlus/components/tables";
import BloqueoGestionModal from "./BloqueoGestionModal";
import {
  CitoPlusLinkIconPrimary,
  CitoPlusLinkIconDanger,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import {
  ZonaSocialSelect,
  useZonaSocialOptions,
} from "jsx/components/CitoPlus/components/ZonaSocial";

const BLOQUEOS_URL = "/api/v3/zonas-sociales/bloqueos/";

const ESTADO_OPTIONS = Object.freeze({
  all: { label: "Todos", badge: "secondary" },
  activo: { label: "Activos", badge: "success" },
  inactivo: { label: "Inactivos", badge: "secondary" },
});

const RANGE_PRESETS = Object.freeze({
  all: {
    label: "Todos",
    getRange: () => ({ start: null, end: null }),
  },
  upcoming_90: {
    label: "Próximos 90 días",
    getRange: () => {
      const now = new Date();
      const end = new Date(now.getTime());
      end.setUTCDate(end.getUTCDate() + 90);
      return { start: now, end };
    },
  },
  past_90: {
    label: "Últimos 90 días",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime());
      start.setUTCDate(start.getUTCDate() - 90);
      return { start, end: now };
    },
  },
  year_window: {
    label: "±180 días",
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime());
      const end = new Date(now.getTime());
      start.setUTCDate(start.getUTCDate() - 180);
      end.setUTCDate(end.getUTCDate() + 180);
      return { start, end };
    },
  },
  custom: {
    label: "Personalizado",
    getRange: () => ({ start: null, end: null }),
  },
});

const ensureDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  try {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (_err) {
    return null;
  }
};

const normalizeBloqueo = (raw) => {
  if (!raw || !raw.uuid) {
    return null;
  }

  const start = raw.fecha_inicio ?? raw.start ?? "";
  const end = raw.fecha_fin ?? raw.end ?? "";

  if (!start || !end) {
    return null;
  }

  return {
    uuid: raw.uuid,
    titulo: raw.titulo || "Bloqueo sin título",
    descripcion: raw.descripcion || "",
    motivo: raw.motivo || "otro",
    start,
    end,
    zonaNombre: raw.zona_nombre || raw.zona?.nombre || "Zona sin nombre",
    activo: raw.activo !== undefined ? Boolean(raw.activo) : true,
  };
};

const BloqueosAdminView = () => {
  const { zones, isLoading: isLoadingZones } = useZonaSocialOptions({
    onError: () => notifyError("No fue posible cargar las zonas sociales."),
  });

  const [bloqueos, setBloqueos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [filters, setFilters] = useState({
    zona: "all",
    estado: "all",
  });
  const [rangePreset, setRangePreset] = useState("all");
  const [dateRange, setDateRange] = useState(RANGE_PRESETS.all.getRange());

  const [modalState, setModalState] = useState({
    isOpen: false,
    uuid: null,
    initialData: null,
  });

  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    uuid: null,
    titulo: "",
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const initialFetchRef = useRef(false);

  const presetOptions = useMemo(
    () =>
      Object.entries(RANGE_PRESETS).map(([value, config]) => ({
        value,
        label: config.label,
      })),
    [],
  );

  const estadoOptions = useMemo(
    () =>
      Object.entries(ESTADO_OPTIONS).map(([value, config]) => ({
        value,
        label: config.label,
      })),
    [],
  );

  const buildRangeParams = useCallback(
    (rangeOverride) => {
      const activeRange = rangeOverride ?? dateRange;
      const { start, end } = activeRange;

      if (!start && !end) {
        return {};
      }

      if (!start || !end) {
        notifyError("Selecciona un rango de fechas completo.");
        return null;
      }

      const normalizedStart = ensureDate(start);
      const normalizedEnd = ensureDate(end);

      if (!normalizedStart || !normalizedEnd) {
        notifyError("Las fechas seleccionadas no son válidas.");
        return null;
      }

      if (normalizedStart.getTime() > normalizedEnd.getTime()) {
        return {
          start: normalizedEnd.toISOString(),
          end: normalizedStart.toISOString(),
        };
      }

      return {
        start: normalizedStart.toISOString(),
        end: normalizedEnd.toISOString(),
      };
    },
    [dateRange],
  );

  const fetchBloqueos = useCallback(
    async (pageIndex = 1, overrides = {}) => {
      const rangeParams = buildRangeParams(overrides.range);
      if (rangeParams === null) {
        return;
      }

      setIsLoading(true);
      setHasSearched(true);

      const safePage =
        typeof totalPages === "number" && totalPages > 0
          ? Math.min(Math.max(1, pageIndex || 1), totalPages)
          : Math.max(1, pageIndex || 1);

      try {
        const params = {
          ...rangeParams,
          page: safePage,
        };

        if (filters.zona && filters.zona !== "all") {
          params.zona = filters.zona;
        }

        if (filters.estado !== "all") {
          params.activo = filters.estado === "activo";
        }

        const ordering =
          overrides.ordering ??
          (sortField
            ? `${order === "desc" ? "-" : ""}${sortField}`
            : undefined);

        if (ordering) {
          params.ordering = ordering;
        }

        const response = await axiosInstance.get(BLOQUEOS_URL, { params });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        const normalized = payload.map(normalizeBloqueo).filter(Boolean);

        const count =
          typeof response.data?.count === "number"
            ? response.data.count
            : Array.isArray(response.data)
              ? response.data.length
              : normalized.length;

        const fetchedTotalPages =
          response.data?.total_pages ??
          (count > 0 ? Math.ceil(count / (response.data?.page_size ?? 25)) : 1);

        setBloqueos(normalized);
        setTotalRecords(count);
        setTotalPages(Math.max(fetchedTotalPages, 1));

        const resolvedPage =
          response.data?.current_page ?? response.data?.page ?? safePage;
        setCurrentPage(resolvedPage);
        setNextUrl(response.data?.next ?? null);
        setPreviousUrl(response.data?.previous ?? null);
      } catch (_error) {
        notifyError(
          "No pudimos obtener los bloqueos de zonas sociales. Inténtalo de nuevo.",
        );
        setBloqueos([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setNextUrl(null);
        setPreviousUrl(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildRangeParams,
      filters.estado,
      filters.zona,
      order,
      sortField,
      totalPages,
    ],
  );

  useEffect(() => {
    if (initialFetchRef.current) {
      return;
    }
    initialFetchRef.current = true;
    fetchBloqueos(1, { range: RANGE_PRESETS.all.getRange() });
  }, [fetchBloqueos]);

  const handleZonaChange = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      zona: newValue || "all",
    }));
  }, []);

  const handleEstadoChange = useCallback((event) => {
    setFilters((prev) => ({
      ...prev,
      estado: event.target.value,
    }));
  }, []);

  const handlePresetChange = useCallback((event) => {
    const presetKey = event.target.value;
    setRangePreset(presetKey);

    if (presetKey === "custom") {
      return;
    }

    const preset = RANGE_PRESETS[presetKey];
    if (!preset) {
      return;
    }

    setDateRange(preset.getRange());
  }, []);

  const handleRangePickerChange = useCallback((values) => {
    const [start, end] = values || [];
    setRangePreset("custom");
    setDateRange({
      start: start ? new Date(start) : null,
      end: end ? new Date(end) : null,
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      zona: "all",
      estado: "all",
    });
    setRangePreset("all");
    setDateRange(RANGE_PRESETS.all.getRange());
    setSortField("");
    setOrder("asc");
    setHasSearched(false);
    fetchBloqueos(1, {
      range: RANGE_PRESETS.all.getRange(),
      ordering: undefined,
    });
  }, [fetchBloqueos]);

  const handleConsult = useCallback(() => {
    fetchBloqueos(1);
  }, [fetchBloqueos]);

  const handleSortingChange = useCallback(
    (column) => {
      if (!column?.id) {
        return;
      }

      const nextOrder =
        sortField === column.id ? (order === "asc" ? "desc" : "asc") : "asc";

      setSortField(column.id);
      setOrder(nextOrder);

      fetchBloqueos(1, {
        ordering: `${nextOrder === "desc" ? "-" : ""}${column.id}`,
      });
    },
    [fetchBloqueos, order, sortField],
  );

  const handleOpenModal = useCallback(
    (bloqueo = null) => {
      const startCandidate = ensureDate(dateRange.start) || new Date();
      const endCandidate = ensureDate(dateRange.end);
      const fallbackEnd = new Date(startCandidate.getTime() + 60 * 60 * 1000);

      const initial = bloqueo ?? {
        titulo: "",
        descripcion: "",
        motivo: "mantenimiento",
        zona_uuid: filters.zona !== "all" ? filters.zona : "",
        start: startCandidate,
        end:
          endCandidate && endCandidate > startCandidate
            ? endCandidate
            : fallbackEnd,
        fecha_inicio: startCandidate,
        fecha_fin:
          endCandidate && endCandidate > startCandidate
            ? endCandidate
            : fallbackEnd,
        all_day: false,
        activo: true,
      };

      setModalState({
        isOpen: true,
        uuid: bloqueo?.uuid ?? null,
        initialData: initial,
      });
    },
    [dateRange.end, dateRange.start, filters.zona],
  );

  const handleCloseModal = useCallback(() => {
    setModalState({
      isOpen: false,
      uuid: null,
      initialData: null,
    });
  }, []);

  const handleModalSaved = useCallback(() => {
    fetchBloqueos(currentPage);
  }, [currentPage, fetchBloqueos]);

  const openDeleteModal = useCallback((bloqueo) => {
    if (!bloqueo?.uuid) return;
    setDeleteModalState({
      isOpen: true,
      uuid: bloqueo.uuid,
      titulo: bloqueo.titulo || "Bloqueo sin título",
    });
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalState({ isOpen: false, uuid: null, titulo: "" });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteModalState.uuid) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`${BLOQUEOS_URL}${deleteModalState.uuid}/`);
      notifySuccess("Bloqueo eliminado correctamente.");
      closeDeleteModal();
      fetchBloqueos(currentPage);
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsDeleting(false);
    }
  }, [closeDeleteModal, currentPage, deleteModalState.uuid, fetchBloqueos]);

  const showEmptyState = hasSearched && !isLoading && bloqueos.length === 0;

  const statusSummary = useMemo(() => {
    const summary = { activos: 0, inactivos: 0 };
    bloqueos.forEach((item) => {
      if (item.activo) {
        summary.activos += 1;
      } else {
        summary.inactivos += 1;
      }
    });
    return summary;
  }, [bloqueos]);

  const columns = useMemo(
    () => [
      {
        Header: "Estado",
        accessor: "activo",
        disableSortBy: false,
        disableFilters: true,
        Cell: ({ value }) => (
          <span className={`badge bg-${value ? "success" : "secondary"}`}>
            {value ? "Activo" : "Inactivo"}
          </span>
        ),
      },
      {
        Header: "Título",
        accessor: "titulo",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <>
            <div className="fw-semibold">{row.original.titulo}</div>
            <div className="text-muted small">
              {row.original.descripcion || "Sin descripción"}
            </div>
          </>
        ),
      },
      {
        Header: "Horario",
        accessor: "start",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <div>
            <div>{row.original.start || "-"}</div>
            <div className="text-muted small">{row.original.end || "-"}</div>
          </div>
        ),
      },
      {
        Header: "Zona social",
        accessor: "zonaNombre",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ value }) => <span className="text-nowrap">{value}</span>,
      },
      {
        Header: "Acciones",
        accessor: "acciones",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => (
          <ButtonGroup aria-label="Acciones de bloqueo">
            <CitoPlusLinkIconPrimary
              title="Editar bloqueo"
              onClick={() => handleOpenModal(row.original)}
            >
              <i className="fas fa-edit" />
            </CitoPlusLinkIconPrimary>
            <CitoPlusLinkIconDanger
              title="Eliminar bloqueo"
              onClick={() => openDeleteModal(row.original)}
            >
              <i className="fas fa-trash" />
            </CitoPlusLinkIconDanger>
          </ButtonGroup>
        ),
      },
    ],
    [handleOpenModal, openDeleteModal],
  );

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">Bloqueos de zonas sociales</h2>
            <div className="text-muted small">
              Activos: {statusSummary.activos.toLocaleString("es-CO")} ·
              Inactivos: {statusSummary.inactivos.toLocaleString("es-CO")}
            </div>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleConsult}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Consultando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-search me-2" />
                  Consultar
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => handleOpenModal(null)}
              disabled={isLoading}
            >
              <i className="fas fa-plus me-2" />
              Nuevo bloqueo
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        <div className="row g-3 mb-4">
          <div className="col-lg-4 col-md-6">
            <ZonaSocialSelect
              value={filters.zona}
              onChange={handleZonaChange}
              allowAllOption
              allOptionValue="all"
              allOptionLabel="Todas las zonas"
              allowEmptyOption={false}
              showLabel
              label="Zona social"
              options={zones}
              loading={isLoadingZones}
              autoFetch={false}
              disabled={isLoadingZones || isLoading}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="filtro-estado" className="form-label">
              Estado
            </label>
            <select
              id="filtro-estado"
              className="form-select"
              value={filters.estado}
              onChange={handleEstadoChange}
              disabled={isLoading}
            >
              {estadoOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="range-preset" className="form-label">
              Rango
            </label>
            <select
              id="preset-rango"
              className="form-select"
              value={rangePreset}
              onChange={handlePresetChange}
              disabled={isLoading}
            >
              {presetOptions.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-4 col-md-6">
            <label className="form-label">Fechas personalizadas</label>
            <DatePickerRanger
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={handleRangePickerChange}
              disabled={rangePreset !== "custom" || isLoading}
              withPortal
              selectsRange
              customInput={
                <input
                  className="form-control"
                  placeholder="Selecciona un rango"
                  readOnly
                />
              }
            />
            {rangePreset !== "custom" && (
              <div className="form-text">
                {rangePreset === "all"
                  ? "Se mostrarán todos los bloqueos (sin rango)."
                  : "El rango lo determina el preset seleccionado."}
              </div>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="py-5 text-center">
            <SpinnerCitoPlus text="Cargando bloqueos de zonas sociales..." />
          </div>
        )}

        {!isLoading && showEmptyState && (
          <div className="alert alert-light border" role="alert">
            No encontramos bloqueos con los criterios seleccionados.
          </div>
        )}

        {!isLoading && !showEmptyState && bloqueos.length > 0 && (
          <Tables
            columns={columns}
            data={bloqueos}
            loading={isLoading}
            totalPages={totalPages}
            currentPage={currentPage}
            totalRecords={totalRecords}
            fetchUrl={fetchBloqueos}
            handleSortingChange={handleSortingChange}
            sortField={sortField}
            order={order}
            nextUrl={nextUrl}
            previousUrl={previousUrl}
          />
        )}

        {!isLoading && !hasSearched && (
          <div className="alert alert-info" role="alert">
            Ajusta los filtros y da clic en <strong>Consultar</strong> para
            visualizar los bloqueos registrados.
          </div>
        )}
      </div>

      <BloqueoGestionModal
        show={modalState.isOpen}
        bloqueoUuid={modalState.uuid}
        onClose={handleCloseModal}
        onSaved={handleModalSaved}
        cachedZones={zones}
        initialData={modalState.initialData}
      />

      <Modal
        show={deleteModalState.isOpen}
        onHide={isDeleting ? undefined : closeDeleteModal}
        centered
        size="sm"
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-danger me-2" />
            Confirmar eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de eliminar el bloqueo{" "}
          <strong>"{deleteModalState.titulo}"</strong>?
          <br />
          <small className="text-muted">Esta acción no se puede deshacer.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDeleteModal}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <i className="fas fa-circle-notch fa-spin me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2" />
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default BloqueosAdminView;
